<script setup lang="ts">
import { computed, ref } from 'vue';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import { token } from '@/refs/common.refs';
import { CampaignService } from '@/services/campaign.services';
import CampaignGalleryGrid from './CampaignGalleryGrid.vue';
import CampaignSingletonMediaSlot from './CampaignSingletonMediaSlot.vue';
import { useCampaignToast } from './useCampaignToast';
import type { ICampaignMedia, ICampaignMediaRole } from './campaign.types';

const props = defineProps<{
  storeId: string;
  campaignId: string;
}>();

const queryClient = useQueryClient();
const { notifySuccess, notifyError } = useCampaignToast();

const {
  data: mediaList,
  isPending: isLoading,
  isError,
} = useQuery({
  queryKey: computed(() => ['campaign-media', props.storeId, props.campaignId]),
  queryFn: () => CampaignService.listMedia(props.storeId, props.campaignId),
  enabled: computed(() => !!props.storeId && !!props.campaignId),
});

const banner = computed(
  () => mediaList.value?.find((m) => m.role === 'banner') ?? null,
);
const thumbnail = computed(
  () => mediaList.value?.find((m) => m.role === 'thumbnail') ?? null,
);
const gallery = computed(
  () =>
    mediaList.value
      ?.filter((m) => m.role === 'gallery')
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder) ?? [],
);

function invalidateMedia() {
  return queryClient.invalidateQueries({
    queryKey: ['campaign-media', props.storeId, props.campaignId],
  });
}

/* ── Upload ─────────────────────────────────────────────────────────────── */
const uploadError = ref('');
const uploadingRole = ref<ICampaignMediaRole | null>(null);

async function uploadCampaignMedia(
  file: File,
  role: ICampaignMediaRole,
  metadata?: Record<string, unknown>,
): Promise<ICampaignMedia> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('role', role);
  if (metadata) fd.append('metadata', JSON.stringify(metadata));
  const r = await axios.post<ICampaignMedia>(
    `${VITE_API_URL}stores/${props.storeId}/campaigns/${props.campaignId}/media`,
    fd,
    { headers: { Authorization: `Bearer ${token.value?.accessToken}` } },
  );
  return r.data;
}

async function handleUpload(file: File, role: ICampaignMediaRole) {
  uploadError.value = '';
  uploadingRole.value = role;
  try {
    await uploadCampaignMedia(file, role);
    await invalidateMedia();
    notifySuccess('Média ajouté.');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    uploadError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? err?.message ?? "Erreur lors de l'upload");
    notifyError(err, "Erreur lors de l'upload du média");
  } finally {
    uploadingRole.value = null;
  }
}

function onGalleryFileChange(e: Event) {
  const files = Array.from((e.target as HTMLInputElement).files ?? []);
  (e.target as HTMLInputElement).value = '';
  files.reduce(
    (chain, file) => chain.then(() => handleUpload(file, 'gallery')),
    Promise.resolve(),
  );
}

/* ── Suppression (aussi utilisée pour "Remplacer" un singleton : l'API rejette
 * un second upload sur un rôle déjà occupé avec 400 "Role already set on this
 * campaign", donc remplacer = supprimer puis ré-uploader, jamais un upload
 * direct par-dessus l'existant) ────────────────────────────────────────── */
const pendingDeleteId = ref<string | null>(null);
const deleteError = ref('');

async function removeMedia(media: ICampaignMedia, opts?: { silent?: boolean }) {
  pendingDeleteId.value = media.id;
  if (!opts?.silent) deleteError.value = '';
  try {
    await CampaignService.removeMedia(
      props.storeId,
      props.campaignId,
      media.id,
    );
    await invalidateMedia();
    if (!opts?.silent) notifySuccess('Média supprimé.');
  } catch (err: any) {
    if (!opts?.silent) {
      const raw = err?.response?.data?.message;
      deleteError.value = Array.isArray(raw)
        ? raw.join(' · ')
        : (raw ?? err?.message ?? 'Impossible de supprimer ce média');
      notifyError(err, 'Impossible de supprimer ce média');
    }
  } finally {
    pendingDeleteId.value = null;
  }
}

const bannerSlotRef = ref<InstanceType<
  typeof CampaignSingletonMediaSlot
> | null>(null);
const thumbnailSlotRef = ref<InstanceType<
  typeof CampaignSingletonMediaSlot
> | null>(null);

async function replaceSingleton(
  media: ICampaignMedia,
  slot: InstanceType<typeof CampaignSingletonMediaSlot> | null,
) {
  await removeMedia(media, { silent: true });
  slot?.openPicker();
}

/* ── Réordonnancement galerie ───────────────────────────────────────────── */
const reordering = ref(false);
const reorderError = ref('');

async function persistGalleryOrder(orderedIds: string[]) {
  reordering.value = true;
  reorderError.value = '';
  try {
    await CampaignService.reorderGalleryMedia(
      props.storeId,
      props.campaignId,
      orderedIds,
    );
    await invalidateMedia();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    reorderError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? err?.message ?? 'Impossible de réordonner la galerie');
    notifyError(err, 'Impossible de réordonner la galerie');
  } finally {
    reordering.value = false;
  }
}

function moveGalleryItem(id: string, direction: -1 | 1) {
  const items = gallery.value.slice();
  const idx = items.findIndex((m) => m.id === id);
  const targetIdx = idx + direction;
  if (idx < 0 || targetIdx < 0 || targetIdx >= items.length) return;
  [items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
  void persistGalleryOrder(items.map((m) => m.id));
}
</script>

<template>
  <div class="cmp">
    <div v-if="isLoading" class="cmp__loading">Chargement des médias…</div>
    <div v-else-if="isError" class="cmp__empty">
      <i class="pi pi-exclamation-circle" />
      <p>Impossible de charger les médias de cette campagne.</p>
    </div>

    <template v-else>
      <p v-if="uploadError" class="cmp__error">{{ uploadError }}</p>
      <p v-if="deleteError" class="cmp__error">{{ deleteError }}</p>
      <p v-if="reorderError" class="cmp__error">{{ reorderError }}</p>

      <CampaignSingletonMediaSlot
        ref="bannerSlotRef"
        title="Bannière"
        :media="banner"
        :uploading="uploadingRole === 'banner'"
        :pending-delete-id="pendingDeleteId"
        @upload="(file) => handleUpload(file, 'banner')"
        @replace="(media) => replaceSingleton(media, bannerSlotRef)"
        @remove="removeMedia"
      />

      <CampaignSingletonMediaSlot
        ref="thumbnailSlotRef"
        title="Vignette"
        compact
        :media="thumbnail"
        :uploading="uploadingRole === 'thumbnail'"
        :pending-delete-id="pendingDeleteId"
        @upload="(file) => handleUpload(file, 'thumbnail')"
        @replace="(media) => replaceSingleton(media, thumbnailSlotRef)"
        @remove="removeMedia"
      />

      <!-- Galerie -->
      <section class="cmp__section">
        <div class="cmp__section-hd">
          <h3 class="cmp__section-title">Galerie</h3>
          <label class="cmp__btn cmp__btn--primary cmp__btn--sm">
            <i
              v-if="uploadingRole === 'gallery'"
              class="pi pi-spin pi-spinner"
            />
            <i v-else class="pi pi-plus" />
            Ajouter à la galerie
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              @change="onGalleryFileChange"
            />
          </label>
        </div>

        <p v-if="!gallery.length" class="cmp__gallery-empty">
          Aucune image dans la galerie.
        </p>
        <CampaignGalleryGrid
          v-else
          :items="gallery"
          :pending-delete-id="pendingDeleteId"
          :reordering="reordering"
          @move-up="(id) => moveGalleryItem(id, -1)"
          @move-down="(id) => moveGalleryItem(id, 1)"
          @remove="removeMedia"
        />
      </section>
    </template>
  </div>
</template>

<style scoped>
.cmp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cmp__loading,
.cmp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.cmp__empty i {
  font-size: 2rem;
  opacity: 0.4;
}
.cmp__error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
  font-size: 0.8125rem;
}
.cmp__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  background: var(--nxt-card-bg);
}
.cmp__section-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.cmp__section-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cmp__gallery-empty {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cmp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.cmp__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}
.cmp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.cmp__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
